import { useMemo, useState } from 'react';
import { DiagnosticTestResult } from '@wa-communication-layer/diagnostics/test-registry';

import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  SkipForward,
  ChevronDown,
  Loader2,
} from 'lucide-react';

// ── Types ───────────────────────────────────────────────────────────────────

interface DiagnosticRunResult {
  results: DiagnosticTestResult[];
  summary: {
    total: number;
    passed: number;
    failed: number;
    errored: number;
    skipped: number;
    running?: number;
  };
  durationMs: number;
  error?: string;
}

interface DiagnosticsResultsTreeProps {
  data: DiagnosticRunResult | null;
  loading?: boolean;
}

const sanitizeInputsForDisplay = (inputs: Record<string, any>): Record<string, any> => {
  const redact = (value: any): any => {
    if (Array.isArray(value)) return value.map(redact);
    if (value && typeof value === 'object') {
      const out: Record<string, any> = {};
      for (const [key, nestedValue] of Object.entries(value)) {
        const normalizedKey = key.toLowerCase();
        if (normalizedKey === 'filedataurl') {
          // Never show fileDataUrl in diagnostics inputs preview.
          continue;
        }
        out[key] = redact(nestedValue);
      }
      return out;
    }
    return value;
  };

  return redact(inputs);
};

// ── Status config ───────────────────────────────────────────────────────────

const statusConfig: Record<string, {
  classes: string;
  icon: React.ReactNode;
  label: string;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
}> = {
  pass: {
    classes: 'bg-green-50 text-green-700 border-green-200',
    icon: <CheckCircle2 className="size-3.5" />,
    label: 'PASS',
    variant: 'secondary',
  },
  fail: {
    classes: 'bg-red-50 text-red-700 border-red-200',
    icon: <XCircle className="size-3.5" />,
    label: 'FAIL',
    variant: 'destructive',
  },
  error: {
    classes: 'bg-amber-50 text-amber-700 border-amber-200',
    icon: <AlertTriangle className="size-3.5" />,
    label: 'ERROR',
    variant: 'outline',
  },
  skipped: {
    classes: 'bg-slate-50 text-slate-500 border-slate-200',
    icon: <SkipForward className="size-3.5" />,
    label: 'SKIP',
    variant: 'secondary',
  },
  running: {
    classes: 'bg-blue-50 text-blue-700 border-blue-200',
    icon: <Loader2 className="size-3.5 animate-spin" />,
    label: 'RUNNING',
    variant: 'secondary',
  },
};

const chatTypeBadge: Record<string, { classes: string; label: string }> = {
  contact: { classes: 'border-blue-300 text-blue-700 bg-blue-50', label: 'Contact' },
  group: { classes: 'border-green-300 text-green-700 bg-green-50', label: 'Group' },
  newsletter: { classes: 'border-purple-300 text-purple-700 bg-purple-50', label: 'Newsletter' },
};

const safetyBadge: Record<string, string> = {
  safe: 'border-green-300 text-green-800',
  write: 'border-amber-300 text-amber-700',
  dangerous: 'border-red-300 text-red-700',
};

// ── StatusBadge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const config = statusConfig[status] ?? statusConfig.error;
  return (
    <Badge variant="outline" className={cn('text-[10px] h-5 gap-1 font-semibold', config.classes)}>
      {config.icon}
      {config.label}
    </Badge>
  );
}

// ── SafetyBadge ─────────────────────────────────────────────────────────────

function SafetyBadge({ safety }: { safety: string }) {
  return (
    <Badge variant="outline" className={cn('text-[10px] h-4', safetyBadge[safety] ?? 'text-slate-500')}>
      {safety}
    </Badge>
  );
}

// ── ChatTypeBadge ───────────────────────────────────────────────────────────

function ChatTypeBadge({ chatType }: { chatType: string }) {
  const config = chatTypeBadge[chatType];
  if (!config) return null;
  return (
    <Badge variant="outline" className={cn('text-[10px] h-4', config.classes)}>
      {config.label}
    </Badge>
  );
}

// ── ProfilePicPreview ────────────────────────────────────────────────────────

function ProfilePicPreview({ data }: { data: Array<{ contactId: string; profilePic?: string }> }) {
  if (!Array.isArray(data) || data.length === 0) return null;

  return (
    <div className="mt-2 space-y-2">
      <p className="text-[11px] font-semibold text-slate-600">Profile Pictures:</p>
      <div className="flex flex-wrap gap-3">
        {data.map((item, idx) => (
          <div key={idx} className="flex flex-col items-center gap-1 p-2 bg-white rounded-lg border shadow-sm">
            {item.profilePic ? (
              <img
                src={item.profilePic}
                alt={`Profile: ${item.contactId}`}
                className="w-16 h-16 rounded-full object-cover border-2 border-slate-200"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-slate-200 flex items-center justify-center text-slate-400 text-[10px]">
                No pic
              </div>
            )}
            <span className="text-[9px] text-slate-500 max-w-[80px] truncate" title={item.contactId}>
              {item.contactId}
            </span>
            {item.profilePic && (
              <a
                href={item.profilePic}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[9px] text-blue-500 hover:underline"
              >
                Open URL
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Helper to detect profile pic response ────────────────────────────────────

function extractProfilePicData(response: any): Array<{ contactId: string; profilePic?: string }> | null {
  // Handle both direct array and { success, data: [...] } wrapper
  const arr = Array.isArray(response)
    ? response
    : response?.success && Array.isArray(response?.data)
      ? response.data
      : null;
  if (!arr || arr.length === 0) return null;
  // Accept both contactId and chatId fields
  const isValid = arr.every(
    (item: any) =>
      typeof item === 'object' &&
      item !== null &&
      ('contactId' in item || 'chatId' in item)
  );
  if (!isValid) return null;
  return arr.map((item: any) => ({
    contactId: item.contactId ?? item.chatId ?? '',
    profilePic: item.profilePic,
  }));
}

// ── TestRow ─────────────────────────────────────────────────────────────────

function TestRow({ result }: { result: DiagnosticTestResult }) {
  const [expanded, setExpanded] = useState(true);
  const safeInputs = result.inputs ? sanitizeInputsForDisplay(result.inputs) : undefined;
  const isRunning = result.status === 'running';

  // Check if this is a profile pic response
  const profilePicData = result.id === 'chat.getProfilePicUrl' ? extractProfilePicData(result.response) : null;

  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        className="flex items-center gap-2 py-1.5 px-2 w-full text-left hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <StatusBadge status={result.status} />
        <span className="flex-1 font-mono text-xs truncate">
          {result.name}
          {result.toolName && (
            <span className="ml-1 text-slate-400">({result.toolName})</span>
          )}
        </span>
        {result.chatType && <ChatTypeBadge chatType={result.chatType} />}
        <SafetyBadge safety={result.safety} />
        {isRunning ? (
          <svg className="animate-spin h-3.5 w-3.5 text-blue-500 min-w-[45px]" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <span className="text-[11px] text-slate-400 min-w-[45px] text-right tabular-nums">
            {result.duration}ms
          </span>
        )}
      </button>

      {expanded && !isRunning && (
        <div className="px-3 pb-2 bg-slate-50/50 space-y-2">
          {safeInputs && Object.keys(safeInputs).length > 0 && (
            <div>
              <p className="text-[11px] font-semibold text-slate-600">Inputs:</p>
              <pre className="font-mono text-[11px] whitespace-pre-wrap break-all text-slate-700 mt-0.5">
                {JSON.stringify(safeInputs, null, 2)}
              </pre>
            </div>
          )}
          {result.error && (
            <div>
              <p className="text-[11px] font-semibold text-red-600">Error:</p>
              <pre className="font-mono text-[11px] whitespace-pre-wrap break-all text-red-700 mt-0.5">
                {result.error.message}
                {result.error.stack && `\n\n${result.error.stack}`}
              </pre>
            </div>
          )}
          <div>
            <p className="text-[11px] font-semibold text-slate-600">Response:</p>
            <pre
              className={cn(
                'font-mono text-[11px] whitespace-pre-wrap break-all mt-0.5 max-h-[600px] overflow-auto',
                result.response === undefined ? 'text-slate-400' : 'text-slate-800'
              )}
            >
              {result.response !== undefined
                ? JSON.stringify(result.response, null, 2)
                : '(no response data)'}
            </pre>
          </div>
          {/* Profile Pic Preview */}
          {profilePicData && <ProfilePicPreview data={profilePicData} />}
        </div>
      )}
    </div>
  );
}

// ── DiagnosticsResultsTree ──────────────────────────────────────────────────

export function DiagnosticsResultsTree({ data, loading }: DiagnosticsResultsTreeProps) {
  const grouped = useMemo(() => {
    if (!data?.results) return {};
    const groups: Record<string, DiagnosticTestResult[]> = {};
    for (const r of data.results) {
      (groups[r.domain] ??= []).push(r);
    }
    return groups;
  }, [data?.results]);

  if (!data) {
    if (loading) {
      return (
        <div className="py-4 space-y-2">
          <Progress className="animate-pulse" />
          <p className="text-xs text-slate-500 text-center">Running diagnostics...</p>
        </div>
      );
    }
    return null;
  }

  if (data.error && !data.results?.length) {
    return (
      <div className="p-3 bg-red-50 rounded-md border border-red-200">
        <p className="text-sm text-red-700">{data.error}</p>
      </div>
    );
  }

  if (!data.results?.length && !data.summary) {
    return (
      <div className="p-3 bg-amber-50 rounded-md border border-amber-200">
        <p className="text-sm text-slate-600 mb-1">No results returned. Raw response:</p>
        <pre className="font-mono text-[11px] whitespace-pre-wrap break-all">
          {JSON.stringify(data, null, 2)}
        </pre>
      </div>
    );
  }

  const summary = data.summary ?? { total: 0, passed: 0, failed: 0, errored: 0, skipped: 0, running: 0 };
  const runningCount = summary.running ?? 0;
  const completedCount = summary.total - runningCount;
  const durationMs = data.durationMs ?? 0;
  return (
    <div className="space-y-2">
      {/* Progress bar when tests are still running */}
      {runningCount > 0 && (
        <div className="space-y-1">
          <Progress value={summary.total > 0 ? (completedCount / summary.total) * 100 : 0} />
          <p className="text-xs text-slate-500 text-center">
            {completedCount}/{summary.total} tests completed
          </p>
        </div>
      )}

      {/* Summary bar */}
      <Card className="flex-row items-center flex-wrap gap-2 py-2 px-3 shadow-none">
        <span className="text-sm font-semibold">Total: {summary.total}</span>
        <Badge variant="secondary" className="bg-green-50 text-green-700 text-[11px]">
          {summary.passed} passed
        </Badge>
        {summary.errored > 0 && (
          <Badge variant="secondary" className="bg-amber-50 text-amber-700 text-[11px]">
            {summary.errored} errors
          </Badge>
        )}
        {summary.failed > 0 && (
          <Badge variant="secondary" className="bg-red-50 text-red-700 text-[11px]">
            {summary.failed} failed
          </Badge>
        )}
        {summary.skipped > 0 && (
          <Badge variant="secondary" className="bg-slate-50 text-slate-500 text-[11px]">
            {summary.skipped} skipped
          </Badge>
        )}
        {runningCount > 0 && (
          <Badge variant="secondary" className="bg-blue-50 text-blue-700 text-[11px]">
            {runningCount} running
          </Badge>
        )}
        <span className="ml-auto text-[11px] text-slate-400 tabular-nums">{durationMs}ms total</span>
      </Card>

      {/* Domain groups */}
      {Object.entries(grouped).map(([domain, results]) => {
        const passed = results.filter(r => r.status === 'pass').length;
        return (
          <Collapsible key={domain} defaultOpen>
            <CollapsibleTrigger className="flex items-center gap-2 w-full py-1.5 px-2 rounded-md hover:bg-slate-50 transition-colors group">
              <ChevronDown className="size-4 text-slate-400 transition-transform group-data-[state=closed]:-rotate-90" />
              <span className="text-sm font-semibold capitalize">{domain}</span>
              <span
                className={cn(
                  'text-xs',
                  passed === results.length ? 'text-green-600' : 'text-amber-600'
                )}
              >
                {passed}/{results.length} passed
              </span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="border rounded-md overflow-hidden mt-1">
                {results.map(r => (
                  <TestRow key={`${r.id}-${r.chatType ?? 'default'}`} result={r} />
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
}
